/**
 * Header inference: is the first row a header, and what should the columns be called?
 */

import type { HeaderRename, InferredHeaders } from "./types";

const NUMERIC = /^[+-]?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?%?$/;
const DATE_LIKE = /^\d{1,4}[-/.]\d{1,2}[-/.]\d{1,4}(?:[T ]\d{1,2}:\d{2}(?::\d{2})?)?$/;

function looksLikeValue(cell: string): boolean {
  const text = cell.trim();
  return NUMERIC.test(text) || DATE_LIKE.test(text);
}

function cleanLabel(raw: string): string {
  return raw
    .replace(/^\uFEFF/, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Decide whether `first` is a header row and produce clean, unique column labels.
 *
 * Heuristic (errs towards "header", which is what exports from real systems have): the first row is NOT
 * a header when at least half of its filled cells look like numbers or dates, or when it is all empty.
 * Pass `{ hasHeader }` to override the guess (the wizard's "first row is a header" toggle).
 *
 * Labels are trimmed and whitespace-collapsed; empty labels become `Column N`; case-insensitive duplicates
 * become `Name (2)`, `Name (3)`. Every change is listed in `renamed` so the UI can show it.
 */
export function inferHeaders(first: readonly string[], options: { hasHeader?: boolean } = {}): InferredHeaders {
  const filled = first.map((cell) => cell.trim()).filter((cell) => cell.length > 0);
  const valueLike = filled.filter(looksLikeValue).length;
  const hasHeader = options.hasHeader ?? (filled.length > 0 && valueLike * 2 < filled.length);

  const renamed: HeaderRename[] = [];
  const headers: string[] = [];
  const seen = new Map<string, number>();

  first.forEach((raw, column) => {
    let label = hasHeader ? cleanLabel(raw) : "";
    if (label.length === 0) {
      label = `Column ${column + 1}`;
      if (hasHeader) renamed.push({ column, from: raw, to: label, reason: "empty" });
    }
    const key = label.toLowerCase();
    const count = (seen.get(key) ?? 0) + 1;
    seen.set(key, count);
    if (count > 1) {
      const unique = `${label} (${count})`;
      renamed.push({ column, from: label, to: unique, reason: "duplicate" });
      label = unique;
      seen.set(label.toLowerCase(), 1);
    }
    headers.push(label);
  });

  return { headers, hasHeader, renamed };
}
